import React, { useState } from 'react';
import { Plus, Crown, Bell } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { ProgressBar } from '../components/ProgressBar';
import { StreakCounter } from '../components/StreakCounter';
import { AICompanion } from '../components/AICompanion';
import { TaskList } from '../components/TaskList';
import { WebLayout } from '../components/WebLayout';
import { Card } from '../components/Card';
import { CreateGoalModal } from '../components/CreateGoalModal';
import { NotificationsModal } from '../components/NotificationsModal';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Trophy, Target, Zap, Flame } from 'lucide-react';
export function HomePage() {
  const {
    stats,
    goals,
    toggleGoal,
    loading
  } = useUser();
  const {
    user
  } = useAuth();
  const {
    tier
  } = useSubscription();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [unreadNotifications] = useState(3); // This will come from notification context
  // Transform goals to tasks format for TaskList
  const tasks = goals.map(g => ({
    id: g.id,
    title: g.title,
    category: g.category,
    duration: '30m',
    completed: g.completed
  }));
  const completedCount = goals.filter(g => g.completed).length;
  const totalCount = goals.length;
  const progress = totalCount > 0 ? completedCount / totalCount * 100 : 0;
  if (loading) {
    return <WebLayout data-id="element-860">
        <div className="flex items-center justify-center min-h-screen" data-id="element-861">
          <div className="text-center" data-id="element-862">
            <div className="w-16 h-16 border-4 border-game-purple border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-863"></div>
            <p className="font-quicksand text-gray-500 dark:text-gray-400" data-id="element-864">
              Loading your quests...
            </p>
          </div>
        </div>
      </WebLayout>;
  }
  return <WebLayout data-id="element-865">
      {/* Mobile Header */}
      <header className="lg:hidden px-6 pt-6 pb-4 flex justify-between items-center sticky top-0 bg-game-bg-pink/80 dark:bg-[#1a1d29]/80 backdrop-blur-sm z-20" data-id="element-866">
        <div className="flex items-center gap-3" data-id="element-867">
          <div className="w-10 h-10 rounded-full bg-game-purple overflow-hidden border-2 border-white" data-id="element-868">
            <img src={user?.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.uid}`} alt="User" className="w-full h-full object-cover" data-id="element-869" />
          </div>
          <div className="flex flex-col" data-id="element-870">
            <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase" data-id="element-871">
              Welcome back
            </span>
            <span className="font-fredoka font-bold text-game-dark dark:text-white" data-id="element-872">
              {user?.displayName || 'QuestZen User'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3" data-id="element-873">
          <StreakCounter count={stats.streak} data-id="element-874" />
          <button className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" onClick={() => navigate('/notifications')} data-id="element-875">
            <Bell className="w-6 h-6 text-gray-600 dark:text-gray-400" data-id="element-876" />
            {unreadNotifications > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-game-coral rounded-full" data-id="element-877"></span>}
          </button>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="hidden lg:block px-8 pt-8 pb-6 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#252936]" data-id="element-878">
        <div className="max-w-7xl mx-auto" data-id="element-879">
          <div className="flex items-center justify-between" data-id="element-880">
            <div data-id="element-881">
              <h1 className="text-3xl font-fredoka font-bold text-game-dark dark:text-white mb-2" data-id="element-882">
                Welcome back, {user?.displayName?.split(' ')[0] || 'there'}! 👋
              </h1>
              <p className="text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-883">
                {completedCount === totalCount && totalCount > 0 ? "Amazing! You've completed all your quests today! 🎉" : `You have ${totalCount - completedCount} quest${totalCount - completedCount !== 1 ? 's' : ''} to complete today`}
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-884">
              <Button size="lg" icon={<Plus className="w-5 h-5" data-id="element-886" />} onClick={() => setShowCreateModal(true)} data-id="element-885">
                New Quest
              </Button>
              <button className="relative p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" onClick={() => setShowNotificationsModal(true)} data-id="element-887">
                <Bell className="w-6 h-6 text-gray-600 dark:text-gray-400" data-id="element-888" />
                {unreadNotifications > 0 && <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-game-coral rounded-full" data-id="element-889"></span>}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="px-6 lg:px-8 py-6 lg:py-8" data-id="element-890">
        <div className="max-w-7xl mx-auto" data-id="element-891">
          {/* Desktop Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-892">
            {/* Left Column - Stats */}
            <div className="lg:col-span-1 space-y-6" data-id="element-893">
              {/* Premium Upgrade CTA - Free Users Only - SINGLE INSTANCE */}
              {tier === 'free' && <Card className="p-6 bg-gradient-to-br from-game-purple via-game-teal to-game-coral text-white relative overflow-hidden cursor-pointer hover:shadow-xl transition-shadow" onClick={() => navigate('/upgrade')} data-id="element-894">
                  <div className="relative z-10" data-id="element-895">
                    <div className="flex items-center gap-2 mb-3" data-id="element-896">
                      <Crown className="w-6 h-6" data-id="element-897" />
                      <h3 className="font-fredoka font-bold text-lg" data-id="element-898">
                        Go Premium
                      </h3>
                    </div>
                    <p className="text-sm opacity-90 font-quicksand mb-4" data-id="element-899">
                      Unlock unlimited quests, advanced features, and more!
                    </p>
                    <div className="flex items-center gap-2 text-sm font-quicksand font-bold" data-id="element-900">
                      <span data-id="element-901">Starting at $6.67/month</span>
                    </div>
                  </div>
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" data-id="element-902"></div>
                </Card>}

              {/* XP Card */}
              <Card className="p-6" data-id="element-903">
                <div className="flex items-center gap-3 mb-4" data-id="element-904">
                  <div className="w-12 h-12 bg-game-yellow rounded-2xl flex items-center justify-center" data-id="element-905">
                    <Trophy className="w-6 h-6 text-white" data-id="element-906" />
                  </div>
                  <div data-id="element-907">
                    <h3 className="font-fredoka font-bold text-lg text-game-dark dark:text-white" data-id="element-908">
                      Level {stats.level}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-909">
                      Productivity Wizard
                    </p>
                  </div>
                </div>
                <ProgressBar current={stats.xp % 1000} max={1000} label="XP to Next Level" data-id="element-910" />
              </Card>

              {/* Quick Stats */}
              <Card className="p-6" data-id="element-911">
                <h3 className="font-fredoka font-bold text-game-dark dark:text-white mb-4" data-id="element-912">
                  Quick Stats
                </h3>
                <div className="space-y-4" data-id="element-913">
                  <div className="flex items-center justify-between" data-id="element-914">
                    <div className="flex items-center gap-3" data-id="element-915">
                      <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center" data-id="element-916">
                        <Flame className="w-5 h-5 text-orange-500" data-id="element-917" />
                      </div>
                      <span className="font-quicksand text-gray-600 dark:text-gray-400" data-id="element-918">
                        Streak
                      </span>
                    </div>
                    <span className="font-fredoka font-bold text-game-dark dark:text-white" data-id="element-919">
                      {stats.streak} days
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-920">
                    <div className="flex items-center gap-3" data-id="element-921">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center" data-id="element-922">
                        <Target className="w-5 h-5 text-blue-500" data-id="element-923" />
                      </div>
                      <span className="font-quicksand text-gray-600 dark:text-gray-400" data-id="element-924">
                        Completed
                      </span>
                    </div>
                    <span className="font-fredoka font-bold text-game-dark dark:text-white" data-id="element-925">
                      {stats.completedGoals}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-926">
                    <div className="flex items-center gap-3" data-id="element-927">
                      <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center" data-id="element-928">
                        <Zap className="w-5 h-5 text-purple-500" data-id="element-929" />
                      </div>
                      <span className="font-quicksand text-gray-600 dark:text-gray-400" data-id="element-930">
                        Focus Sessions
                      </span>
                    </div>
                    <span className="font-fredoka font-bold text-game-dark dark:text-white" data-id="element-931">
                      {stats.focusSessions}
                    </span>
                  </div>
                </div>
              </Card>

              {/* AI Companion - Desktop */}
              <div className="hidden lg:block" data-id="element-932">
                <AICompanion message={completedCount === totalCount && totalCount > 0 ? 'Wow! You crushed everything today! Ready for a break? 🌟' : "Hey friend! Let's tackle that next quest. You've got this! 🚀"} data-id="element-933" />
              </div>
            </div>

            {/* Right Column - Tasks */}
            <div className="lg:col-span-2 space-y-6" data-id="element-934">
              {/* AI Companion - Mobile */}
              <div className="lg:hidden" data-id="element-935">
                <AICompanion message={completedCount === totalCount && totalCount > 0 ? 'Wow! You crushed everything today! Ready for a break? 🌟' : "Hey friend! Let's tackle that next quest. You've got this! 🚀"} data-id="element-936" />
              </div>

              {/* Tasks Section */}
              <div data-id="element-937">
                <div className="flex justify-between items-center mb-6" data-id="element-938">
                  <h2 className="text-2xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-939">
                    Today's Quests
                  </h2>
                  <span className="font-quicksand font-bold text-sm text-gray-400 bg-white dark:bg-[#252936] px-4 py-2 rounded-full border border-gray-200 dark:border-gray-800" data-id="element-940">
                    {completedCount}/{totalCount} Done
                  </span>
                </div>

                <TaskList tasks={tasks} onToggle={toggleGoal} data-id="element-941" />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Floating Action Button - Mobile Only */}
      <div className="lg:hidden fixed bottom-24 right-6 z-30" data-id="element-942">
        <Link to="/create-goal" data-id="element-943">
          <motion.button whileHover={{
          scale: 1.1
        }} whileTap={{
          scale: 0.9
        }} className="bg-game-dark text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center border-4 border-white dark:border-[#1a1d29]" data-id="element-944">
            <Plus className="w-6 h-6" data-id="element-945" />
          </motion.button>
        </Link>
      </div>

      {/* Create Goal Modal - Desktop Only */}
      <CreateGoalModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} data-id="element-946" />

      {/* Notifications Modal - Desktop Only */}
      <NotificationsModal isOpen={showNotificationsModal} onClose={() => setShowNotificationsModal(false)} data-id="element-947" />
    </WebLayout>;
}